// ─────────────────────────────────────────────────────────────────────────────
// exportPDF.js — Export d'une annonce en PDF via window.print()
// Génère une page d'impression HTML optimisée, ouvre la fenêtre d'impression.
// Aucune librairie externe requise.
// Usage :
//   import { exportAnnoncePDF } from "../utils/exportPDF";
//   exportAnnoncePDF(product);
// ─────────────────────────────────────────────────────────────────────────────

export function exportAnnoncePDF(product) {
  if (!product) return;

  const ficheTechniqueRows = product.ficheTechnique
    ? Object.entries(product.ficheTechnique)
        .map(([k, v]) => `
          <tr>
            <td style="padding:8px 12px;background:#f8fafc;font-weight:600;color:#374151;border:1px solid #e5e7eb;width:40%">${k}</td>
            <td style="padding:8px 12px;color:#1f2937;border:1px solid #e5e7eb">${v}</td>
          </tr>`)
        .join("")
    : "";

  const caracteristiquesHTML = product.caracteristiques?.length
    ? `<ul style="margin:0;padding-left:20px;columns:2;gap:20px">
        ${product.caracteristiques.map(c => `<li style="margin-bottom:6px;color:#4b5563;font-size:13px">${c}</li>`).join("")}
       </ul>`
    : "";

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8"/>
  <title>MarinaDZ — ${product.nom}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1f2937; padding: 30px; max-width: 800px; margin: 0 auto; }
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
      @page { margin: 1.5cm; }
    }
    .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #0d9488; padding-bottom: 16px; margin-bottom: 24px; }
    .logo { font-size: 24px; font-weight: 800; color: #1f2937; }
    .logo span { color: #0d9488; }
    .ref { font-size: 12px; color: #6b7280; }
    .main-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
    .product-img { width: 100%; height: 220px; object-fit: cover; border-radius: 12px; border: 1px solid #e5e7eb; }
    .info h1 { font-size: 20px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
    .badge { display: inline-block; background: #f0fdfa; color: #0d9488; padding: 3px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; text-transform: uppercase; margin-bottom: 10px; }
    .prix { font-size: 28px; font-weight: 800; color: #0d9488; margin: 12px 0; }
    .meta-row { display: flex; gap: 16px; margin-top: 12px; flex-wrap: wrap; }
    .meta-item { font-size: 12px; color: #6b7280; }
    .meta-item strong { color: #374151; }
    .section { margin-bottom: 20px; }
    .section h2 { font-size: 15px; font-weight: 700; color: #1f2937; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 12px; }
    .desc { font-size: 13px; color: #4b5563; line-height: 1.7; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .footer { margin-top: 30px; padding-top: 16px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; font-size: 11px; color: #9ca3af; }
    .print-btn { display: block; margin: 0 auto 24px; padding: 12px 28px; background: #0d9488; color: white; border: none; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; }
  </style>
</head>
<body>

  <button class="print-btn no-print" onclick="window.print()">🖨️ Imprimer / Sauvegarder en PDF</button>

  <div class="header">
    <div class="logo">Marina<span>DZ</span></div>
    <div class="ref">
      <div>Référence : REF-${String(product.id).padStart(6, "0")}</div>
      <div>Date : ${new Date().toLocaleDateString("fr-FR")}</div>
    </div>
  </div>

  <div class="main-grid">
    <div>
      <img src="${product.image}" alt="${product.nom}" class="product-img" onerror="this.style.display='none'"/>
    </div>
    <div class="info">
      <div class="badge">${product.categorie}</div>
      <h1>${product.nom}</h1>
      <p class="desc">${product.description}</p>
      <div class="prix">${product.prix}</div>
      <div class="meta-row">
        ${product.marque ? `<div class="meta-item"><strong>Marque :</strong> ${product.marque}</div>` : ""}
        ${product.localisation ? `<div class="meta-item"><strong>Localisation :</strong> ${product.localisation}</div>` : ""}
        <div class="meta-item"><strong>État :</strong> ${product.isNew ? "Neuf" : "Occasion"}</div>
        ${product.entreprise ? `<div class="meta-item"><strong>Exposant :</strong> ${product.entreprise}</div>` : ""}
      </div>
    </div>
  </div>

  ${product.descriptionDetaillee ? `
  <div class="section">
    <h2>Description détaillée</h2>
    <p class="desc">${product.descriptionDetaillee}</p>
  </div>` : ""}

  ${caracteristiquesHTML ? `
  <div class="section">
    <h2>Caractéristiques principales</h2>
    ${caracteristiquesHTML}
  </div>` : ""}

  ${ficheTechniqueRows ? `
  <div class="section">
    <h2>Fiche technique</h2>
    <table>${ficheTechniqueRows}</table>
  </div>` : ""}

  <div class="footer">
    <span>MarinaDZ — La plateforme maritime B2B en Algérie</span>
    <span>www.marinadz.com</span>
    <span>contact@marinadz.com</span>
  </div>

</body>
</html>`;

  const win = window.open("", "_blank", "width=900,height=700");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

export default exportAnnoncePDF;